import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import {
  ArrowRight,
  Search,
  ShieldCheck,
  ClipboardList,
  Users,
  Sparkles,
  Truck,
  Trash2,
  Hammer,
  Drill,
  Building2,
  Wrench,
  Compass,
  MapPin,
  Clock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import hero512 from "@/assets/begner-landing-pic-512.webp";
import hero1024 from "@/assets/begner-landing-pic-1024.webp";
import heroMobile400 from "@/assets/begner-landing-mobile-400.webp";
import heroMobile672 from "@/assets/begner-landing-mobile-672.webp";
import heroMobile800 from "@/assets/begner-landing-mobile-800.webp";
import hero768 from "@/assets/begner-landing-pic-768.webp";
import heroImage from "@/assets/begner-landing-pic-1200.webp";
import hero1600 from "@/assets/begner-landing-pic-1600.webp";
import heroAvif512 from "@/assets/begner-landing-pic-512.avif";
import heroAvif768 from "@/assets/begner-landing-pic-768.avif";
import heroAvif1024 from "@/assets/begner-landing-pic-1024.avif";
import heroAvif1200 from "@/assets/begner-landing-pic-1200.avif";
import heroAvif1600 from "@/assets/begner-landing-pic-1600.avif";
import heroMobileAvif400 from "@/assets/begner-landing-mobile-400.avif";
import heroMobileAvif672 from "@/assets/begner-landing-mobile-672.avif";
import heroMobileAvif800 from "@/assets/begner-landing-mobile-800.avif";
import { magazineImages } from "@/lib/magazine-images";
import { LatestJobsSection } from "@/components/jobs/latest-jobs-section";
import { ProviderTestimonialsSection } from "@/components/provider-testimonials-section";
import { SectionHeading } from "@/components/section-heading";
import { useLanguage } from "@/lib/i18n";
import { articleSummaries } from "@/data/article-summaries";

const mobileHeroSizes = "calc(100vw - 32px)";
const desktopHeroSizes =
  "(min-width: 1200px) 497px, (min-width: 1024px) calc((100vw - 96px) * 0.45), (min-width: 640px) calc(100vw - 48px), calc(100vw - 32px)";
const mobileHeroAvifSources = `${heroMobileAvif400} 400w, ${heroMobileAvif672} 672w, ${heroMobileAvif800} 800w`;
const desktopHeroAvifSources = `${heroAvif512} 512w, ${heroAvif768} 768w, ${heroAvif1024} 1024w, ${heroAvif1200} 1200w, ${heroAvif1600} 1600w`;

const homeTitle = "Handwerker in der Schweiz finden & vergleichen | Begner";
const homeDescription =
  "Geprüfte Handwerker in der Schweiz finden: Vergleichen Sie echte Kundenbewertungen und Richtpreise. Auftrag beschreiben und kostenlos Offerten erhalten.";

export const Route = createFileRoute("/")({
  staticData: { sitemap: true },
  head: () => ({
    meta: [
      { title: homeTitle },
      {
        name: "description",
        content: homeDescription,
      },
      { property: "og:title", content: homeTitle },
      {
        property: "og:description",
        content: homeDescription,
      },
      { name: "twitter:title", content: homeTitle },
      { name: "twitter:description", content: homeDescription },
    ],
  }),
  component: Home,
});

const categoryIcons = [
  Sparkles,
  Truck,
  Sparkles,
  Trash2,
  Hammer,
  Drill,
  Building2,
  Wrench,
  Building2,
  Compass,
] as const;
const categoryIds = [
  "reinigung",
  "reinigung",
  "reinigung",
  "raeumung",
  "raeumung",
  "raeumung",
  "bauunternehmen",
  "bauunternehmen",
  "bauunternehmen",
  "bauunternehmen",
] as const;
const categoryUsesQuery = [false, true, true, false, true, true, false, true, true, true] as const;

const stepIcons = [ClipboardList, Users, ShieldCheck] as const;
const aboutIcons = [ShieldCheck, ClipboardList, Compass] as const;

const homeArticleSlugs = [
  "eigenmietwert-berechnen-rechner-schweiz",
  "badumbau-in-6-wochen-realistischer-bauablauf",
  "waermepumpe-statt-oelheizung-lohnt-sich-wechsel",
] as const;

type SearchMode = "contractors" | "calculator" | "tips";

const searchModes: SearchMode[] = ["contractors", "calculator", "tips"];

const searchAliases = [
  {
    match: ["kernbohrung", "bohrung", "betonbohren", "carottage", "carotaggio", "core drilling"],
    contractorCategory: "kernbohrung",
    priceService: "kernbohrung",
    priceTab: "bau",
    articleCategory: "neubau",
  },
  {
    match: [
      "reinigung",
      "putzen",
      "endreinigung",
      "umzugsreinigung",
      "nettoyage",
      "pulizia",
      "cleaning",
    ],
    contractorCategory: "reinigung",
    priceService: "reinigung",
    priceTab: "umzug",
    articleCategory: "reinigung",
  },
  {
    match: ["umzug", "zuegeln", "zügeln", "demenagement", "déménagement", "trasloco", "moving"],
    contractorCategory: "umzug",
    priceService: "umzug",
    priceTab: "umzug",
    articleCategory: "umzug",
  },
  {
    match: [
      "sanitär",
      "sanitaer",
      "bad",
      "badezimmer",
      "sanitaire",
      "sanitari",
      "plumbing",
      "bathroom",
    ],
    contractorCategory: "sanitaer",
    priceService: "sanitaer",
    priceTab: "sanitaer",
    articleCategory: "sanitaer",
  },
  {
    match: [
      "elektriker",
      "elektro",
      "strom",
      "electricien",
      "électricien",
      "elettricista",
      "electrician",
    ],
    contractorCategory: "elektriker",
    priceService: "elektriker",
    priceTab: "elektro",
    articleCategory: "haustechnik",
  },
  {
    match: ["parkett", "vinyl", "boden", "sol", "pavimento", "flooring"],
    priceService: "parkett",
    priceTab: "boden",
    articleCategory: "boden",
  },
  {
    match: ["fassade", "maler", "malerei", "peinture", "pittura", "facade", "painting"],
    priceService: "fassade",
    priceTab: "malerei",
    articleCategory: "fassade",
  },
] as const;

function findSearchAlias(query: string) {
  const normalized = query.trim().toLowerCase();
  return searchAliases.find((alias) => alias.match.some((term) => normalized.includes(term)));
}

const nearbyRegions = [
  {
    name: { de: "Basel", fr: "Bâle", it: "Basilea", en: "Basel" },
    canton: "bs",
    slug: "basel",
  },
  {
    name: { de: "Baselland", fr: "Bâle-Campagne", it: "Basilea Campagna", en: "Basel-Landschaft" },
    canton: "bl",
    slug: "baselland",
  },
  {
    name: { de: "Solothurn", fr: "Soleure", it: "Soletta", en: "Solothurn" },
    canton: "so",
    slug: "solothurn",
  },
  {
    name: { de: "Aargau", fr: "Argovie", it: "Argovia", en: "Aargau" },
    canton: "ag",
    slug: "aargau",
  },
  {
    name: { de: "Zürich", fr: "Zurich", it: "Zurigo", en: "Zurich" },
    canton: "zh",
    slug: "zuerich",
  },
  {
    name: { de: "Bern", fr: "Berne", it: "Berna", en: "Bern" },
    canton: "be",
    slug: "bern",
  },
  {
    name: { de: "Luzern", fr: "Lucerne", it: "Lucerna", en: "Lucerne" },
    canton: "lu",
    slug: "luzern",
  },
  {
    name: { de: "St. Gallen", fr: "Saint-Gall", it: "San Gallo", en: "St. Gallen" },
    canton: "sg",
    slug: "st-gallen",
  },
  {
    name: { de: "Chur", fr: "Coire", it: "Coira", en: "Chur" },
    canton: "gr",
    slug: "chur",
  },
  {
    name: { de: "Lausanne", fr: "Lausanne", it: "Losanna", en: "Lausanne" },
    canton: "vd",
    slug: "lausanne",
  },
  {
    name: { de: "Genf", fr: "Genève", it: "Ginevra", en: "Geneva" },
    canton: "ge",
    slug: "genf",
  },
  {
    name: { de: "Lugano", fr: "Lugano", it: "Lugano", en: "Lugano" },
    canton: "ti",
    slug: "lugano",
  },
];

const homeCopy = {
  de: {
    heroTitle: "Handwerker in der Schweiz",
    heroHighlight: "einfach finden.",
    heroText:
      "Mit echten Kundenbewertungen. Beschreiben Sie Ihren Auftrag und erhalten Sie kostenlos Offerten von geprüften Auftragnehmern in der Schweiz.",
    searchLabel: "Ich suche:",
    searchOptions: ["Auftragnehmer, Offerte", "Baukostenrechner", "Tipps"],
    searchPlaceholder: "Was benötigen Sie? Z. B. Reinigung, Umzug, Elektriker…",
    searchButton: "Suchen",
    requestLink: "Stellen Sie kostenlos hier Ihre Anfrage.",
    heroImageAlt: "Handwerker bei der Holzbearbeitung in einer Werkstatt",
    stats: [
      { value: "100+", label: "Bewertungen" },
      { value: "100+", label: "Bauunternehmen" },
      { value: "10+", label: "Leistungskategorien" },
      { value: "48h", label: "erste Offerte" },
    ],
    servicesEyebrow: "Leistungen",
    servicesTitle: "Für jedes Bauvorhaben den richtigen Profi",
    servicesText:
      "Wählen Sie eine Leistung und entdecken Sie passende Auftragnehmer aus Ihrer Region.",
    providerEyebrow: "Für Unternehmen",
    providerTitle: "Sie bieten diese Leistungen an?",
    providerText:
      "Präsentieren Sie Ihr Unternehmen, gewinnen Sie passende Aufträge und wählen Sie transparent das Paket, das zu Ihrem Wachstum passt.",
    providerPrimary: "Anbieterpreise vergleichen",
    providerSecondary: "Als Anbieter registrieren",
    categories: [
      "Reinigung",
      "Umzugsreinigung",
      "Baureinigung",
      "Räumung",
      "Rückbau",
      "Entkernung",
      "Bauunternehmen",
      "Generalunternehmen",
      "Totalunternehmen",
      "Projektentwicklung",
    ],
    nearbyTitle: "Die besten Profis in Ihrer Nähe – schweizweit",
    howEyebrow: "So funktioniert's",
    howTitle: "In 3 Schritten zum passenden Handwerker",
    stepPrefix: "Schritt",
    steps: [
      {
        title: "Auftrag beschreiben",
        text: "Stellen Sie Ihre Anfrage in wenigen Minuten, kostenlos und unverbindlich.",
      },
      {
        title: "Offerten erhalten",
        text: "Geprüfte Auftragnehmer aus Ihrer Region melden sich mit konkreten Angeboten.",
      },
      {
        title: "Vergleichen & beauftragen",
        text: "Lesen Sie echte Bewertungen, vergleichen Sie Preise und wählen Sie den richtigen Profi.",
      },
    ],
    postJob: "Auftrag kostenlos einstellen",
    aboutEyebrow: "Über Begner",
    aboutTitle: "Gute Arbeit beginnt mit der richtigen Verbindung.",
    aboutLead:
      "Begner bringt Menschen mit passenden Handwerkern, Bauunternehmen und Immobilienprofis zusammen – einfach, transparent und schweizweit.",
    aboutText:
      "Wir schaffen Orientierung, wo Entscheidungen schnell unübersichtlich werden. Mit aussagekräftigen Profilen, echten Bewertungen und verständlichen Richtpreisen helfen wir Ihnen, Angebote sicher zu vergleichen und den richtigen Profi für Ihr Vorhaben zu finden.",
    aboutCardEyebrow: "Dafür stehen wir",
    aboutCardTitle: "Klar vergleichen. Sicher entscheiden.",
    aboutValues: [
      {
        title: "Vertrauen vor Vermittlung",
        text: "Geprüfte Profile und echte Rückmeldungen geben Ihnen eine verlässliche Grundlage.",
      },
      {
        title: "Transparenz von Anfang an",
        text: "Klare Informationen und Richtpreise machen Angebote besser vergleichbar.",
      },
      {
        title: "In der ganzen Schweiz",
        text: "Wir verbinden Sie mit passenden Profis aus Ihrer Region und allen Sprachgebieten.",
      },
    ],
    aboutPrimary: "Projekt kostenlos starten",
    aboutSecondary: "Profis entdecken",
    planningEyebrow: "Gut vorbereitet",
    planningTitle: "Handwerker-Offerten besser vergleichen",
    planningText:
      "Ob Reinigung, Umzug oder Renovation: Je klarer Sie Ihr Vorhaben beschreiben, desto leichter können Betriebe den Aufwand einschätzen. Diese Punkte helfen Ihnen bei der Anfrage und bei der Auswahl.",
    planningTips: [
      {
        title: "Eine aussagekräftige Anfrage erstellen",
        text: "Beschreiben Sie die Arbeiten, den Ort und den gewünschten Zeitraum. Ergänzen Sie, soweit bekannt, Flächen, Mengen und Materialien sowie Ihr Budget. Fotos vom aktuellen Zustand helfen, den Umfang einzuschätzen. Persönliche Kontaktdaten und die genaue Privatadresse gehören nicht in die öffentliche Beschreibung.",
      },
      {
        title: "Leistungen statt nur Endpreise vergleichen",
        text: "Prüfen Sie, ob die Offerten dieselben Arbeiten umfassen. Sind Material, Anfahrt und Entsorgung enthalten? Welche Positionen werden nach Aufwand berechnet? Klären Sie offene Fragen direkt mit dem Betrieb. Richtpreise geben eine erste Orientierung; der konkrete Preis hängt vom Zustand vor Ort und vom vereinbarten Leistungsumfang ab.",
      },
      {
        title: "Den passenden Betrieb auswählen",
        text: "Vergleichen Sie Spezialisierung, Tätigkeitsregion und Kundenbewertungen. Fragen Sie nach Referenzen für ähnliche Projekte und klären Sie die Verfügbarkeit. Halten Sie vor der Beauftragung den Leistungsumfang, den Preis und den vorgesehenen Termin schriftlich fest. So lassen sich unterschiedliche Erwartungen früh klären.",
      },
    ],
    planningPrices: "Richtpreise zur Orientierung",
    planningProviders: "Betriebe im Verzeichnis ansehen",
    magazineEyebrow: "Inspiration & Wissen",
    magazineTitle: "Unser Magazin",
    magazineText: "Praktische Ideen und Orientierung für Ihr nächstes Projekt.",
    allArticles: "Alle Artikel ansehen",
    magazineArticles: [
      {
        category: "Renovieren",
        title: "Fenster ersetzen: Diese fünf Fragen klären Sie vor der Offerte",
        readingTime: "4 Min.",
      },
      {
        category: "Haus & Garten",
        title: "Mehr aus dem Aussenbereich machen – auch auf kleiner Fläche",
        readingTime: "5 Min.",
      },
      {
        category: "Badezimmer",
        title: "Ein Bad planen, das im Alltag wirklich funktioniert",
        readingTime: "6 Min.",
      },
    ],
    ctaTitle: "Bereit für Ihr nächstes Bauprojekt?",
    ctaText:
      "Beschreiben Sie Ihren Auftrag. Sie erhalten kostenlos und unverbindlich mehrere Offerten von geprüften Auftragnehmern.",
    ctaPrimary: "Anfrage eingeben",
    ctaSecondary: "Auftragnehmer ansehen",
  },
  fr: {
    heroTitle: "Le moyen le plus simple",
    heroHighlight: "de trouver de bons artisans.",
    heroText:
      "Avec de vrais avis clients. Décrivez votre mandat et recevez gratuitement des offres de prestataires vérifiés en Suisse.",
    searchLabel: "Je cherche:",
    searchOptions: ["Prestataires, offres", "Calculateur de coûts", "Conseils"],
    searchPlaceholder: "De quoi avez-vous besoin? P. ex. nettoyage, déménagement, électricien…",
    searchButton: "Rechercher",
    requestLink: "Déposez gratuitement votre demande ici.",
    heroImageAlt: "Artisan travaillant le bois dans un atelier",
    stats: [
      { value: "100+", label: "Avis" },
      { value: "100+", label: "Entreprises du bâtiment" },
      { value: "10+", label: "Catégories de services" },
      { value: "48h", label: "Première offre" },
    ],
    servicesEyebrow: "Services",
    servicesTitle: "Le bon professionnel pour chaque projet",
    servicesText:
      "Choisissez une prestation et découvrez des prestataires adaptés de votre région.",
    providerEyebrow: "Pour les entreprises",
    providerTitle: "Vous proposez ces prestations?",
    providerText:
      "Présentez votre entreprise, gagnez des mandats adaptés et choisissez en toute transparence l’offre qui accompagne votre croissance.",
    providerPrimary: "Comparer les prix professionnels",
    providerSecondary: "S’inscrire comme prestataire",
    categories: [
      "Nettoyage",
      "Nettoyage de déménagement",
      "Nettoyage de chantier",
      "Débarras",
      "Déconstruction",
      "Curage",
      "Entreprise de construction",
      "Entreprise générale",
      "Entreprise totale",
      "Développement de projets",
    ],
    nearbyTitle: "Les meilleurs pros près de chez vous, dans toute la Suisse",
    howEyebrow: "Comment ça marche",
    howTitle: "En 3 étapes vers le bon artisan",
    stepPrefix: "Étape",
    steps: [
      {
        title: "Décrire le mandat",
        text: "Déposez votre demande en quelques minutes, gratuitement et sans engagement.",
      },
      {
        title: "Recevoir des offres",
        text: "Des prestataires vérifiés de votre région vous répondent avec des offres concrètes.",
      },
      {
        title: "Comparer et mandater",
        text: "Consultez de vrais avis, comparez les prix et choisissez le bon professionnel.",
      },
    ],
    postJob: "Publier un mandat gratuitement",
    aboutEyebrow: "À propos de Begner",
    aboutTitle: "Un travail de qualité commence par la bonne rencontre.",
    aboutLead:
      "Begner met en relation particuliers, artisans, entreprises de construction et professionnels de l’immobilier, simplement, en toute transparence et partout en Suisse.",
    aboutText:
      "Nous apportons de la clarté là où les décisions deviennent vite complexes. Grâce à des profils détaillés, de vrais avis et des prix indicatifs compréhensibles, vous pouvez comparer les offres sereinement et trouver le bon professionnel pour votre projet.",
    aboutCardEyebrow: "Nos engagements",
    aboutCardTitle: "Comparer clairement. Décider sereinement.",
    aboutValues: [
      {
        title: "La confiance avant tout",
        text: "Des profils vérifiés et des retours authentiques vous donnent une base fiable.",
      },
      {
        title: "La transparence dès le départ",
        text: "Des informations claires et des prix indicatifs facilitent la comparaison.",
      },
      {
        title: "Partout en Suisse",
        text: "Nous vous mettons en relation avec des professionnels de votre région et de toutes les régions linguistiques.",
      },
    ],
    aboutPrimary: "Démarrer un projet gratuitement",
    aboutSecondary: "Découvrir les professionnels",
    planningEyebrow: "Bien préparer son projet",
    planningTitle: "Mieux comparer les offres des artisans",
    planningText:
      "Nettoyage, déménagement ou rénovation: plus votre demande est précise, plus les entreprises peuvent évaluer le travail à réaliser. Ces conseils vous aident à préparer votre demande et à choisir un prestataire.",
    planningTips: [
      {
        title: "Rédiger une demande précise",
        text: "Décrivez les travaux, la localité et la période souhaitée. Précisez, si vous les connaissez, les surfaces, les quantités, les matériaux et votre budget. Des photos de l’état actuel aident à évaluer l’ampleur du projet. Ne publiez pas vos coordonnées personnelles ni votre adresse privée exacte dans la description publique.",
      },
      {
        title: "Comparer les prestations et pas seulement les totaux",
        text: "Vérifiez que les offres couvrent les mêmes travaux. Les matériaux, les déplacements et l’évacuation sont-ils compris? Quels postes sont facturés selon le temps passé? Clarifiez les points ouverts avec l’entreprise. Les prix indicatifs donnent un premier repère; le prix concret dépend de l’état des lieux et des prestations convenues.",
      },
      {
        title: "Choisir l’entreprise adaptée",
        text: "Comparez les spécialités, la zone d’activité et les avis clients. Demandez des références pour des projets similaires et vérifiez les disponibilités. Avant de confier le mandat, confirmez par écrit les prestations, le prix et la date prévue. Vous pourrez ainsi clarifier les attentes de chacun dès le départ.",
      },
    ],
    planningPrices: "Consulter les prix indicatifs",
    planningProviders: "Voir les entreprises de l’annuaire",
    magazineEyebrow: "Inspiration & savoir",
    magazineTitle: "Notre magazine",
    magazineText: "Des idées pratiques et des repères pour votre prochain projet.",
    allArticles: "Voir tous les articles",
    magazineArticles: [
      {
        category: "Rénovation",
        title: "Remplacer les fenêtres: cinq questions à clarifier avant l'offre",
        readingTime: "4 min",
      },
      {
        category: "Maison & jardin",
        title: "Valoriser les espaces extérieurs, même sur une petite surface",
        readingTime: "5 min",
      },
      {
        category: "Salle de bain",
        title: "Planifier une salle de bain vraiment pratique au quotidien",
        readingTime: "6 min",
      },
    ],
    ctaTitle: "Prêt pour votre prochain projet de construction?",
    ctaText:
      "Décrivez votre mandat. Vous recevrez gratuitement et sans engagement plusieurs offres de prestataires vérifiés.",
    ctaPrimary: "Publier un mandat",
    ctaSecondary: "Voir les prestataires",
  },
  it: {
    heroTitle: "Il modo più semplice",
    heroHighlight: "per trovare buoni artigiani.",
    heroText:
      "Con recensioni reali dei clienti. Descriva il suo incarico e riceva gratuitamente offerte da imprese verificate in Svizzera.",
    searchLabel: "Cerco:",
    searchOptions: ["Imprese, offerte", "Calcolatore costi", "Consigli"],
    searchPlaceholder: "Di cosa ha bisogno? Es. pulizia, trasloco, elettricista…",
    searchButton: "Cerca",
    requestLink: "Invii qui gratuitamente la sua richiesta.",
    heroImageAlt: "Artigiano al lavoro sul legno in un laboratorio",
    stats: [
      { value: "100+", label: "Recensioni" },
      { value: "100+", label: "Imprese edili" },
      { value: "10+", label: "Categorie di servizi" },
      { value: "48h", label: "Prima offerta" },
    ],
    servicesEyebrow: "Servizi",
    servicesTitle: "Il professionista giusto per ogni progetto",
    servicesText: "Scelga una prestazione e scopra imprese adatte della sua regione.",
    providerEyebrow: "Per le imprese",
    providerTitle: "Offre questi servizi?",
    providerText:
      "Presenti la sua impresa, acquisisca incarichi adatti e scelga con trasparenza il pacchetto giusto per crescere.",
    providerPrimary: "Confronta i prezzi professionali",
    providerSecondary: "Registrati come impresa",
    categories: [
      "Pulizia",
      "Pulizia trasloco",
      "Pulizia cantiere",
      "Sgombero",
      "Demolizione",
      "Sventramento",
      "Impresa di costruzioni",
      "Impresa generale",
      "Impresa totale",
      "Sviluppo progetti",
    ],
    nearbyTitle: "I migliori professionisti vicino a lei, in tutta la Svizzera",
    howEyebrow: "Come funziona",
    howTitle: "In 3 passi verso l'artigiano adatto",
    stepPrefix: "Passo",
    steps: [
      {
        title: "Descrivere l'incarico",
        text: "Invii la sua richiesta in pochi minuti, gratuitamente e senza impegno.",
      },
      {
        title: "Ricevere offerte",
        text: "Imprese verificate della sua regione rispondono con offerte concrete.",
      },
      {
        title: "Confrontare e incaricare",
        text: "Legga recensioni reali, confronti i prezzi e scelga il professionista giusto.",
      },
    ],
    postJob: "Pubblica un incarico gratis",
    aboutEyebrow: "Chi è Begner",
    aboutTitle: "Un buon lavoro inizia dall’incontro giusto.",
    aboutLead:
      "Begner mette in contatto persone, artigiani, imprese di costruzione e professionisti immobiliari in modo semplice, trasparente e in tutta la Svizzera.",
    aboutText:
      "Facciamo chiarezza quando le decisioni diventano complesse. Con profili completi, recensioni autentiche e prezzi indicativi comprensibili, la aiutiamo a confrontare le offerte con sicurezza e a trovare il professionista adatto al suo progetto.",
    aboutCardEyebrow: "I nostri valori",
    aboutCardTitle: "Confrontare con chiarezza. Decidere con sicurezza.",
    aboutValues: [
      {
        title: "La fiducia prima di tutto",
        text: "Profili verificati e riscontri autentici offrono una base affidabile.",
      },
      {
        title: "Trasparenza fin dall’inizio",
        text: "Informazioni chiare e prezzi indicativi rendono le offerte più facili da confrontare.",
      },
      {
        title: "In tutta la Svizzera",
        text: "La mettiamo in contatto con professionisti della sua regione e di tutte le aree linguistiche.",
      },
    ],
    aboutPrimary: "Avvia un progetto gratuitamente",
    aboutSecondary: "Scopri i professionisti",
    planningEyebrow: "Preparare bene il progetto",
    planningTitle: "Confrontare meglio le offerte degli artigiani",
    planningText:
      "Pulizia, trasloco o ristrutturazione: più precisa è la descrizione del progetto, più facilmente le imprese possono stimare il lavoro. Questi consigli la aiutano a preparare la richiesta e a scegliere un’impresa.",
    planningTips: [
      {
        title: "Preparare una richiesta dettagliata",
        text: "Descriva i lavori, la località e il periodo desiderato. Aggiunga, se noti, superfici, quantità, materiali e budget. Le foto dello stato attuale aiutano a valutare l’entità del progetto. Non inserisca recapiti personali né l’indirizzo privato esatto nella descrizione pubblica.",
      },
      {
        title: "Confrontare le prestazioni, non solo il totale",
        text: "Verifichi che le offerte comprendano gli stessi lavori. Materiali, trasferte e smaltimento sono inclusi? Quali voci vengono fatturate in base al tempo impiegato? Chiarisca i punti aperti con l’impresa. I prezzi indicativi offrono un primo riferimento; il prezzo concreto dipende dallo stato dei luoghi e dalle prestazioni concordate.",
      },
      {
        title: "Scegliere l’impresa adatta",
        text: "Confronti specializzazioni, zona di attività e recensioni dei clienti. Chieda referenze per progetti simili e verifichi la disponibilità. Prima di assegnare l’incarico, confermi per iscritto prestazioni, prezzo e data prevista. In questo modo è possibile chiarire fin dall’inizio le aspettative di entrambe le parti.",
      },
    ],
    planningPrices: "Consultare i prezzi indicativi",
    planningProviders: "Vedere le imprese nell’elenco",
    magazineEyebrow: "Ispirazione & sapere",
    magazineTitle: "La nostra rivista",
    magazineText: "Idee pratiche e orientamento per il suo prossimo progetto.",
    allArticles: "Vedi tutti gli articoli",
    magazineArticles: [
      {
        category: "Ristrutturare",
        title: "Sostituire le finestre: cinque domande prima dell'offerta",
        readingTime: "4 min",
      },
      {
        category: "Casa & giardino",
        title: "Valorizzare gli spazi esterni, anche su superfici ridotte",
        readingTime: "5 min",
      },
      {
        category: "Bagno",
        title: "Progettare un bagno davvero funzionale nella vita quotidiana",
        readingTime: "6 min",
      },
    ],
    ctaTitle: "Pronto per il suo prossimo progetto edile?",
    ctaText:
      "Descriva il suo incarico. Riceverà gratuitamente e senza impegno più offerte da imprese verificate.",
    ctaPrimary: "Pubblica un incarico",
    ctaSecondary: "Vedi imprese",
  },
  en: {
    heroTitle: "The easiest way",
    heroHighlight: "to find great tradespeople.",
    heroText:
      "Backed by genuine customer reviews. Describe your job and receive free quotes from verified contractors in Switzerland.",
    searchLabel: "I’m looking for:",
    searchOptions: ["Contractors and quotes", "Construction cost calculator", "Advice"],
    searchPlaceholder: "What do you need? E.g. cleaning, moving, electrician…",
    searchButton: "Search",
    requestLink: "Submit your request here free of charge.",
    heroImageAlt: "Tradesperson woodworking in a workshop",
    stats: [
      { value: "100+", label: "Reviews" },
      { value: "100+", label: "Construction companies" },
      { value: "10+", label: "Service categories" },
      { value: "48h", label: "First quote" },
    ],
    servicesEyebrow: "Services",
    servicesTitle: "The right professional for every project",
    servicesText: "Choose a service and discover suitable contractors in your region.",
    providerEyebrow: "For businesses",
    providerTitle: "Do you provide these services?",
    providerText:
      "Showcase your business, win suitable jobs and transparently choose the plan that fits your growth.",
    providerPrimary: "Compare provider pricing",
    providerSecondary: "Register as a provider",
    categories: [
      "Cleaning",
      "End-of-tenancy cleaning",
      "Construction cleaning",
      "Clearance",
      "Demolition",
      "Strip-out",
      "Construction companies",
      "General contractors",
      "Total contractors",
      "Project development",
    ],
    nearbyTitle: "The best professionals near you, across Switzerland",
    howEyebrow: "How it works",
    howTitle: "The right tradesperson in 3 steps",
    stepPrefix: "Step",
    steps: [
      {
        title: "Describe the job",
        text: "Submit your request in just a few minutes, free of charge and without obligation.",
      },
      {
        title: "Receive quotes",
        text: "Verified contractors in your region respond with specific proposals.",
      },
      {
        title: "Compare and appoint",
        text: "Read genuine reviews, compare prices and choose the right professional.",
      },
    ],
    postJob: "Post a job for free",
    aboutEyebrow: "About Begner",
    aboutTitle: "Great work starts with the right connection.",
    aboutLead:
      "Begner connects people with suitable tradespeople, construction companies and real estate professionals – simply, transparently and across Switzerland.",
    aboutText:
      "We provide clarity when decisions quickly become complex. Detailed profiles, genuine reviews and understandable guide prices help you compare quotes with confidence and find the right professional for your project.",
    aboutCardEyebrow: "What we stand for",
    aboutCardTitle: "Compare clearly. Decide confidently.",
    aboutValues: [
      {
        title: "Trust comes first",
        text: "Verified profiles and genuine feedback give you a reliable basis for your decision.",
      },
      {
        title: "Transparency from the start",
        text: "Clear information and guide prices make quotes easier to compare.",
      },
      {
        title: "Across Switzerland",
        text: "We connect you with suitable professionals from your region and every language area.",
      },
    ],
    aboutPrimary: "Start a project for free",
    aboutSecondary: "Discover professionals",
    planningEyebrow: "Plan ahead",
    planningTitle: "Compare tradespeople’s quotes with confidence",
    planningText:
      "Whether you need cleaning, moving or renovation work, a clear description helps businesses estimate the work involved. These tips help you prepare your request and choose a contractor.",
    planningTips: [
      {
        title: "Write a detailed request",
        text: "Describe the work, the town or area and your preferred timeframe. Include areas, quantities, materials and your budget where known. Photos of the current condition help clarify the scope. Keep personal contact details and your exact private address out of the public description.",
      },
      {
        title: "Compare the scope, as well as the total price",
        text: "Check that quotes cover the same work. Are materials, travel and waste disposal included? Which items are charged by time spent? Clarify any open questions with the business. Guide prices provide an initial reference; the actual price depends on conditions at the property and the agreed scope of work.",
      },
      {
        title: "Choose a suitable contractor",
        text: "Compare specialisms, service areas and customer reviews. Ask for references from similar projects and check availability. Before appointing a contractor, confirm the scope, price and planned date in writing. This helps clarify everyone’s expectations from the start.",
      },
    ],
    planningPrices: "Explore guide prices",
    planningProviders: "View businesses in the directory",
    magazineEyebrow: "Inspiration & expertise",
    magazineTitle: "Our magazine",
    magazineText: "Practical ideas and guidance for your next project.",
    allArticles: "View all articles",
    magazineArticles: [
      {
        category: "Renovation",
        title: "Replacing windows: five questions to answer before requesting a quote",
        readingTime: "4 min",
      },
      {
        category: "Home & garden",
        title: "Make more of your outdoor space, even when it is small",
        readingTime: "5 min",
      },
      {
        category: "Bathroom",
        title: "Planning a bathroom that really works in everyday life",
        readingTime: "6 min",
      },
    ],
    ctaTitle: "Ready for your next construction project?",
    ctaText:
      "Describe your job and receive multiple free, no-obligation quotes from verified contractors.",
    ctaPrimary: "Post a job",
    ctaSecondary: "View contractors",
  },
} as const;

function Home() {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [searchMode, setSearchMode] = useState<SearchMode>("contractors");
  const [query, setQuery] = useState("");
  const copy = homeCopy[language];
  const categories = copy.categories.map((label, index) => ({
    label,
    id: categoryIds[index],
    icon: categoryIcons[index],
    query: categoryUsesQuery[index] ? label : undefined,
  }));
  const steps = copy.steps.map((step, index) => ({
    ...step,
    icon: stepIcons[index],
  }));
  const magazineArticles = homeArticleSlugs
    .map((slug) => articleSummaries.find((article) => article.slug === slug))
    .filter((article): article is NonNullable<typeof article> => Boolean(article))
    .map((article) => ({
      slug: article.slug,
      title: article.title,
      category: article.category,
      readingTime: article.readingTime,
      image: magazineImages[article.imageIndex],
    }));
  const aboutValues = copy.aboutValues.map((value, index) => ({
    ...value,
    icon: aboutIcons[index],
  }));

  return (
    <>
      {/* HERO */}
      <section className="relative overflow-hidden bg-surface">
        <div aria-hidden className="absolute inset-0 bg-hero-glow opacity-60" />
        <div className="container-page relative grid grid-cols-1 gap-8 py-10 sm:py-14 md:gap-12 md:py-24 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)] lg:items-center">
          <div className="min-w-0">
            <h1 className="font-display text-[2rem] font-bold leading-[1.08] tracking-tight text-foreground sm:text-5xl md:text-6xl lg:text-7xl">
              {copy.heroTitle}{" "}
              <span className="bg-gradient-brand bg-clip-text text-transparent">
                {copy.heroHighlight}
              </span>
            </h1>
            <p className="mt-5 max-w-xl text-base leading-7 text-muted-foreground sm:text-lg">
              {copy.heroText}
            </p>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                const trimmedQuery = query.trim();
                const alias = findSearchAlias(trimmedQuery);

                if (searchMode === "calculator") {
                  navigate({
                    to: "/preise",
                    search: {
                      q: trimmedQuery || undefined,
                      service: alias?.priceService,
                      tab: alias?.priceTab,
                    },
                  });
                  return;
                }

                if (searchMode === "tips") {
                  navigate({
                    to: "/artikel",
                    search: {
                      q: trimmedQuery || undefined,
                      category: alias?.articleCategory,
                    },
                  });
                  return;
                }

                navigate({
                  to: "/auftragnehmer",
                  search: {
                    q: trimmedQuery || undefined,
                    category:
                      alias && "contractorCategory" in alias ? alias.contractorCategory : undefined,
                  },
                });
              }}
              className="mt-7 min-w-0 rounded-2xl border border-border bg-card p-3 shadow-card sm:mt-10 md:p-4"
            >
              <div className="-mx-1 flex max-w-full snap-x items-center gap-2 overflow-x-auto px-1 pb-3 text-sm text-muted-foreground [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                <span className="shrink-0 font-medium text-foreground">{copy.searchLabel}</span>
                {copy.searchOptions.map((option, index) => {
                  const mode = searchModes[index];
                  const isActive = searchMode === mode;
                  return (
                    <button
                      key={option}
                      type="button"
                      onClick={() => setSearchMode(mode)}
                      className={`min-h-10 shrink-0 snap-start rounded-full px-4 py-1.5 text-sm font-semibold transition ${
                        isActive
                          ? "bg-primary text-primary-foreground shadow-brand"
                          : "font-medium hover:bg-muted"
                      }`}
                      aria-pressed={isActive}
                    >
                      {option}
                    </button>
                  );
                })}
              </div>
              <div className="flex flex-col gap-2 md:flex-row">
                <div className="relative min-w-0 flex-1">
                  <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    type="text"
                    aria-label={copy.searchPlaceholder}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={copy.searchPlaceholder}
                    className="h-14 rounded-xl border-border pl-12 text-base"
                  />
                </div>
                <Button
                  type="submit"
                  size="lg"
                  className="h-12 w-full rounded-xl px-8 text-base font-semibold hover:bg-primary sm:h-14 md:w-auto"
                >
                  {copy.searchButton}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
              <p className="mt-3 px-2 text-sm text-muted-foreground">
                <Link
                  to="/auftrag-einstellen"
                  className="font-medium text-primary underline-offset-4 hover:underline"
                >
                  {copy.requestLink}
                </Link>
              </p>
            </form>
          </div>

          <div className="relative min-w-0">
            <div className="relative overflow-hidden rounded-3xl border border-border bg-card shadow-card">
              <picture>
                <source
                  type="image/avif"
                  media="(max-width: 639px)"
                  srcSet={mobileHeroAvifSources}
                  sizes={mobileHeroSizes}
                  width={800}
                  height={1000}
                />
                <source
                  type="image/webp"
                  media="(max-width: 639px)"
                  srcSet={`${heroMobile400} 400w, ${heroMobile672} 672w, ${heroMobile800} 800w`}
                  sizes={mobileHeroSizes}
                  width={800}
                  height={1000}
                />
                <source
                  type="image/avif"
                  srcSet={desktopHeroAvifSources}
                  sizes={desktopHeroSizes}
                />
                <img
                  src={heroImage}
                  srcSet={`${hero512} 512w, ${hero768} 768w, ${hero1024} 1024w, ${heroImage} 1200w, ${hero1600} 1600w`}
                  sizes={desktopHeroSizes}
                  alt={copy.heroImageAlt}
                  width={1200}
                  height={1800}
                  fetchPriority="high"
                  loading="eager"
                  className="aspect-[4/5] w-full object-cover object-top sm:aspect-auto"
                />
              </picture>
            </div>
          </div>
        </div>
      </section>

      {/* Temporarily disabled: stats section
      <section className="border-y border-border bg-background">
        <div className="container-page grid grid-cols-2 gap-8 py-12 md:grid-cols-4">
          {copy.stats.map((s) => (
            <div key={s.label} className="text-center">
              <p className="font-display text-3xl font-bold text-brand-deep md:text-4xl">
                {s.value}
              </p>
              <p className="mt-2 text-sm text-muted-foreground">{s.label}</p>
            </div>
          ))}
        </div>
      </section>
      */}

      <LatestJobsSection />

      {/* CATEGORIES */}
      <section className="bg-surface py-14 md:py-20">
        <div className="container-page">
          <SectionHeading
            eyebrow={copy.servicesEyebrow}
            title={copy.servicesTitle}
            description={copy.servicesText}
            align="center"
            className="mx-auto max-w-2xl"
          />

          <div className="mt-8 grid grid-cols-2 gap-3 sm:mt-12 sm:grid-cols-3 sm:gap-4 lg:grid-cols-5">
            {categories.map((c) => {
              const Icon = c.icon;
              return (
                <Link
                  key={c.label}
                  to="/auftragnehmer"
                  search={{ category: c.id, q: c.query }}
                  className="group flex min-h-32 flex-col items-center justify-center gap-3 rounded-2xl border border-border bg-card p-4 text-center shadow-card transition hover:-translate-y-1 hover:border-brand hover:shadow-brand sm:p-6"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-soft text-brand-deep transition group-hover:bg-brand group-hover:text-primary-foreground">
                    <Icon className="h-6 w-6" />
                  </div>
                  <span className="text-sm font-semibold text-foreground">{c.label}</span>
                </Link>
              );
            })}
          </div>

          <div className="relative mt-10 overflow-hidden rounded-3xl border border-brand/20 bg-brand-soft/65 p-5 shadow-card sm:mt-12 sm:p-9">
            <div
              aria-hidden
              className="absolute -right-16 -top-20 h-56 w-56 rounded-full bg-brand/15 blur-3xl"
            />
            <div className="relative grid gap-7 lg:grid-cols-[auto_minmax(0,1fr)_auto] lg:items-center">
              <span className="grid h-14 w-14 place-items-center rounded-2xl bg-background text-brand-deep shadow-sm">
                <Building2 className="h-7 w-7" />
              </span>
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-brand-deep">
                  {copy.providerEyebrow}
                </p>
                <h3 className="mt-2 font-display text-2xl font-bold sm:text-3xl">
                  {copy.providerTitle}
                </h3>
                <p className="mt-3 max-w-2xl leading-7 text-muted-foreground">
                  {copy.providerText}
                </p>
              </div>
              <div className="flex flex-col gap-3 sm:flex-row lg:flex-col [&_a]:w-full sm:[&_a]:w-auto">
                <Button asChild size="lg" className="rounded-xl font-semibold shadow-brand">
                  <Link to="/anbieter-preise">
                    {copy.providerPrimary}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="rounded-xl bg-background/80 font-semibold"
                >
                  <Link to="/registrieren">{copy.providerSecondary}</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-background py-14 md:py-20">
        <div className="container-page">
          <SectionHeading
            eyebrow={copy.howEyebrow}
            title={copy.howTitle}
            align="center"
            className="mx-auto max-w-2xl"
          />

          <div className="mt-10 grid gap-7 md:mt-12 md:grid-cols-3 md:gap-6">
            {steps.map((s, i) => {
              const Icon = s.icon;
              return (
                <div
                  key={s.title}
                  className="group relative rounded-2xl border border-border bg-card p-6 shadow-card transition hover:-translate-y-1 hover:border-brand hover:shadow-brand sm:p-8"
                >
                  <div className="absolute -top-4 left-8 rounded-full bg-gradient-brand px-3 py-1 text-xs font-bold text-primary-foreground shadow-brand">
                    {copy.stepPrefix} {i + 1}
                  </div>
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-soft text-brand-deep transition group-hover:bg-brand group-hover:text-primary-foreground">
                    <Icon className="h-6 w-6" />
                  </div>
                  <h3 className="mt-5 font-display text-xl font-bold">{s.title}</h3>
                  <p className="mt-3 text-muted-foreground">{s.text}</p>
                </div>
              );
            })}
          </div>

          <div className="mt-12 flex justify-center">
            <Button asChild size="lg" className="rounded-xl px-8 font-semibold shadow-brand">
              <Link to="/auftrag-einstellen">
                {copy.postJob}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <ProviderTestimonialsSection />

      {/* ABOUT */}
      <section
        id="ueber-uns"
        className="scroll-mt-24 overflow-hidden border-y border-border bg-surface py-14 md:py-24"
      >
        <div className="container-page grid gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:items-center lg:gap-16">
          <div>
            <SectionHeading
              eyebrow={copy.aboutEyebrow}
              title={copy.aboutTitle}
              className="max-w-xl"
            />
            <p className="mt-6 max-w-xl text-lg font-medium leading-relaxed text-foreground/85">
              {copy.aboutLead}
            </p>
            <p className="mt-4 max-w-xl leading-relaxed text-muted-foreground">{copy.aboutText}</p>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap [&_a]:w-full sm:[&_a]:w-auto">
              <Button asChild size="lg" className="rounded-xl font-semibold shadow-brand">
                <Link to="/auftrag-einstellen">
                  {copy.aboutPrimary}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="rounded-xl font-semibold">
                <Link to="/auftragnehmer">{copy.aboutSecondary}</Link>
              </Button>
            </div>
          </div>

          <div className="relative">
            <div aria-hidden className="absolute -inset-10 rounded-full bg-brand/10 blur-3xl" />
            <div className="relative overflow-hidden rounded-3xl border border-brand/20 bg-card shadow-card">
              <div className="bg-gradient-brand px-7 py-7 text-primary-foreground md:px-9 md:py-8">
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-primary-foreground/75">
                  {copy.aboutCardEyebrow}
                </p>
                <h3 className="mt-2 font-display text-2xl font-bold md:text-3xl">
                  {copy.aboutCardTitle}
                </h3>
              </div>

              <div className="divide-y divide-border px-5 sm:px-7 md:px-9">
                {aboutValues.map((value) => {
                  const Icon = value.icon;
                  return (
                    <div key={value.title} className="flex gap-3 py-5 sm:gap-4 sm:py-6">
                      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand-soft text-brand-deep">
                        <Icon className="h-5 w-5" aria-hidden />
                      </div>
                      <div>
                        <h4 className="font-display text-lg font-bold text-foreground">
                          {value.title}
                        </h4>
                        <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                          {value.text}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* NEARBY PROFESSIONALS */}
      <section className="border-t border-border bg-background py-12 md:py-16">
        <div className="container-page text-center">
          <h2 className="font-display text-2xl font-bold md:text-3xl">{copy.nearbyTitle}</h2>

          <div className="mx-auto mt-8 grid max-w-5xl grid-cols-2 gap-x-3 gap-y-5 sm:mt-9 sm:grid-cols-3 sm:gap-x-6 lg:grid-cols-4">
            {nearbyRegions.map((region) => (
              <Link
                key={region.name.de}
                to="/handwerker/$stadt"
                params={{ stadt: region.slug }}
                className="inline-flex min-w-0 items-center justify-center gap-1.5 text-center text-sm font-medium text-foreground transition hover:text-[#f27824] sm:text-base"
              >
                <MapPin className="h-5 w-5 shrink-0 text-[#f27824]" />
                {region.name[language]}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* PROJECT PLANNING */}
      <section className="border-y border-border bg-surface py-14 md:py-20">
        <div className="container-page grid gap-10 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] lg:gap-16">
          <div className="min-w-0">
            <SectionHeading
              eyebrow={copy.planningEyebrow}
              title={copy.planningTitle}
              description={copy.planningText}
              descriptionClassName="leading-7"
            />
            <div className="mt-6 flex flex-col items-start gap-4">
              <Link
                to="/preise"
                className="inline-flex items-center gap-2 text-sm font-semibold text-brand-deep underline-offset-4 hover:underline"
              >
                {copy.planningPrices}
                <ArrowRight className="h-4 w-4 shrink-0" aria-hidden />
              </Link>
              <Link
                to="/auftragnehmer"
                className="inline-flex items-center gap-2 text-sm font-semibold text-brand-deep underline-offset-4 hover:underline"
              >
                {copy.planningProviders}
                <ArrowRight className="h-4 w-4 shrink-0" aria-hidden />
              </Link>
            </div>
          </div>
          <div className="min-w-0 space-y-6">
            {copy.planningTips.map((tip) => (
              <div key={tip.title} className="border-b border-border pb-6 last:border-0 last:pb-0">
                <h3 className="font-display text-xl font-bold text-foreground">{tip.title}</h3>
                <p className="mt-3 leading-7 text-muted-foreground">{tip.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MAGAZINE */}
      <section className="bg-background py-14 md:py-20">
        <div className="container-page">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <SectionHeading
              eyebrow={copy.magazineEyebrow}
              title={copy.magazineTitle}
              description={copy.magazineText}
              className="max-w-2xl"
            />
            <Link
              to="/artikel"
              className="inline-flex items-center gap-1 text-sm font-semibold text-brand-deep transition hover:text-brand"
            >
              {copy.allArticles}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {magazineArticles.map((article) => (
              <Link
                key={article.slug}
                to="/artikel/$slug"
                params={{ slug: article.slug }}
                className="group block"
              >
                <div className="aspect-[1.55] overflow-hidden bg-muted">
                  <img
                    src={article.image.src}
                    srcSet={article.image.srcSet}
                    sizes="(min-width: 1200px) 368px, (min-width: 768px) calc((100vw - 96px) / 3), (min-width: 640px) calc(100vw - 48px), calc(100vw - 32px)"
                    width={1024}
                    height={661}
                    loading="lazy"
                    decoding="async"
                    alt={article.image.alt[language]}
                    className="h-full w-full object-cover transition duration-300 group-hover:scale-105"
                  />
                </div>
                <div className="pt-4">
                  <div className="flex items-center justify-between gap-3 text-xs font-semibold text-brand-deep">
                    <span>{article.category}</span>
                    <span className="inline-flex items-center gap-1 text-muted-foreground">
                      <Clock className="h-3.5 w-3.5" />
                      {article.readingTime}
                    </span>
                  </div>
                  <h3 className="mt-2 font-display text-xl font-bold leading-snug text-foreground transition group-hover:text-brand-deep">
                    {article.title}
                  </h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden bg-gradient-hero py-14 md:py-20">
        <div aria-hidden className="absolute inset-0 bg-cta-glow opacity-30" />
        <div className="container-page relative grid gap-8 md:grid-cols-[1.4fr_1fr] md:items-center">
          <div>
            <h2 className="font-display text-3xl font-bold text-white md:text-4xl">
              {copy.ctaTitle}
            </h2>
            <p className="mt-4 max-w-xl text-white/85">{copy.ctaText}</p>
          </div>
          <div className="flex flex-col justify-start gap-3 sm:flex-row md:justify-end [&_a]:w-full sm:[&_a]:w-auto">
            <Button asChild size="lg" variant="secondary" className="font-semibold">
              <Link to="/auftrag-einstellen">
                {copy.ctaPrimary}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-white/40 bg-transparent text-white hover:bg-white/10 hover:text-white"
            >
              <Link to="/auftragnehmer">{copy.ctaSecondary}</Link>
            </Button>
          </div>
        </div>
      </section>
    </>
  );
}
